// Phase 129 — Demo chat panel.
//
// Renders the conversation transcript (inbound + outbound bubbles) and a
// composer input + Send button. Below the chat is the "Get Started Now For
// Free" handoff CTA that mints a JWT via web-api POST /demo/handoff and
// redirects to app.hookmyapp.com/signup?handoff=<jwt>.
//
// Per Blocker 4, mintHandoffJwt throws UserFacingError on failure; the catch
// here falls back to a plain signup URL (no handoff) so the user can still
// proceed without losing the click.
//
// `dimmed` is set true by demo-section when the takeover overlay is showing —
// chat is visually grayed out and pointer-events disabled while the overlay
// holds the foreground.
import { useEffect, useRef, useState } from 'react';
import { Instagram, MessageCircle } from 'lucide-react';

import { Input } from '../ui/input';
import type { BoundSender, DemoMessage } from './use-demo-stream';
import { mintHandoffJwt, buildHandoffUrl } from '../../lib/handoff';
import { emit } from '../../lib/analytics';

interface DemoChatPanelProps {
  demoSessionId: string;
  messages: DemoMessage[];
  onSend: (text: string) => void;
  dimmed?: boolean;
  boundPhone?: string | null;
  /**
   * Phase 131 — provider-aware bound sender (WhatsApp or Instagram). When set,
   * the chrome renders a provider icon and "Connected To {WhatsApp|Instagram}"
   * label. Null falls back to the legacy "Connected + formatted phone" header
   * driven off `boundPhone` so WA-only deploys are unaffected.
   */
  boundSender?: BoundSender | null;
  onChangeNumber?: () => void;
}

function formatBoundPhone(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const digits = raw.startsWith('+') ? raw.slice(1) : raw;
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 12 && digits.startsWith('972')) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`;
  }
  return `+${digits}`;
}

export function DemoChatPanel({
  demoSessionId,
  messages,
  onSend,
  dimmed,
  boundPhone,
  boundSender,
  onChangeNumber,
}: DemoChatPanelProps) {
  const [draft, setDraft] = useState('');
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = transcriptRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const handleSend = (): void => {
    const text = draft.trim();
    if (!text) return;
    onSend(text);
    setDraft('');
  };

  const handleHandoff = async (): Promise<void> => {
    emit('demo_handoff_clicked', { demo_session_id: demoSessionId });
    try {
      const jwt = await mintHandoffJwt(demoSessionId);
      window.location.href = buildHandoffUrl(jwt);
    } catch {
      // mintHandoffJwt throws UserFacingError on non-2xx (Blocker 4).
      // Fall back to plain signup URL — user can still proceed without JWT.
      window.location.href = buildHandoffUrl('');
    }
  };

  const containerClass = `rounded-[24px] bg-[#181A1B] border border-white/10 p-6 md:p-8 shadow-[0_12px_28px_rgba(0,0,0,0.2),0_2px_4px_rgba(0,0,0,0.1)] flex flex-col h-[422px] transition-opacity duration-200 ${
    dimmed ? 'pointer-events-none opacity-40' : ''
  }`.trim();

  const formattedBoundPhone = formatBoundPhone(boundPhone);
  const isInstagram = boundSender?.senderType === 'instagram';
  // Phase 131 — provider-aware "Connected To {WhatsApp|Instagram}" chrome.
  // Falls back to the legacy "Connected + phone" header when `boundSender`
  // isn't provided (older callers, server still emitting WA-only payloads).
  const providerLabel = isInstagram ? 'Connected To Instagram' : 'Connected To WhatsApp';
  const ProviderIcon = isInstagram ? Instagram : MessageCircle;

  return (
    <div className={containerClass} aria-hidden={dimmed ? 'true' : undefined}>
      <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)]"
          />
          {boundSender ? (
            <>
              <ProviderIcon
                aria-hidden="true"
                className="size-3.5 text-white/70"
                strokeWidth={2}
              />
              <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/50">
                {providerLabel}
              </span>
              {!isInstagram && formattedBoundPhone ? (
                <span className="truncate text-xs font-medium tabular-nums text-white/75">
                  {formattedBoundPhone}
                </span>
              ) : null}
            </>
          ) : (
            <>
              <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-white/50">
                Connected
              </span>
              {formattedBoundPhone ? (
                <span className="truncate text-xs font-medium tabular-nums text-white/75">
                  {formattedBoundPhone}
                </span>
              ) : null}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChangeNumber?.()}
          className="shrink-0 rounded-full border border-white/15 bg-white/[0.04] px-3 py-1 text-xs font-medium text-white/75 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0064E0]"
        >
          Reset
        </button>
      </div>
      <div
        ref={transcriptRef}
        className="space-y-2 flex-1 min-h-0 overflow-y-auto scrollbar-hide pr-2"
      >
        {messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <p className="max-w-[320px] text-base md:text-lg leading-relaxed text-white/60">
              You can now send and receive messages in real time.
            </p>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={m.outbound ? 'text-right' : 'text-left'}>
              <span
                className={`inline-block max-w-[65%] text-left px-4 py-2 rounded-[16px] text-sm ${
                  m.outbound
                    ? 'bg-[#0064E0]/20 border border-[#0064E0]/40 text-white'
                    : 'bg-white/8 border border-white/10 text-white/90'
                }`}
              >
                {m.text}
              </span>
            </div>
          ))
        )}
      </div>
      <div className="mt-4 flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a message"
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSend();
          }}
        />
        <button
          onClick={handleSend}
          className="rounded-[100px] bg-white hover:bg-white/90 active:bg-white/80 px-5 py-2 text-sm font-medium text-[#181A1B] transition"
        >
          Send
        </button>
      </div>
      <div className="mt-8 text-center">
        <a
          onClick={handleHandoff}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') void handleHandoff();
          }}
          className="cursor-pointer inline-flex items-center justify-center rounded-[100px] bg-[#0064E0] hover:bg-[#0143B5] active:bg-[#004BB9] text-white px-8 py-4 text-base font-medium transition"
        >
          Get Started Now For Free
        </a>
      </div>
    </div>
  );
}
